// cart-drawer-custom.js
// Custom interactions for Cart Drawer (Inline Editing, Order Note Saving, and Total Price Sync)

(function () {
  // Regex pattern to extract formatting placeholders like {{amount}} or {{amount_no_decimals}} from currency format string
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  // A helper function to fall back to a default value if the provided value is null, undefined, or NaN
  function defaultTo(value2, defaultValue) {
    // If value2 is null, undefined, or is NaN, return the defaultValue; otherwise, return value2
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  // Formats a raw numerical price into a string with specified decimal and thousands separators
  function formatWithDelimiters(number, precision, thousands, decimal) {
    // Default the precision argument to 2 decimals if not explicitly specified
    precision = defaultTo(precision, 2);
    // Default the thousands separator to a comma if not explicitly specified
    thousands = defaultTo(thousands, ",");
    // Default the decimal separator to a dot if not explicitly specified
    decimal = defaultTo(decimal, ".");
    // If the provided number is not a valid number or is null, return 0 as a fallback
    if (isNaN(number) || number == null) {
      // Return 0
      return 0;
    }
    // Divide the cents by 100 and format to the specified floating point precision
    number = (number / 100).toFixed(precision);
    // Split the formatted number string into integer and fractional decimal parts
    let parts = number.split("."),
        // Apply thousands delimiter to the integer portion using regular expression matching
        dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
        // If a fractional part exists, append the decimal separator followed by the fractional digits
        centsAmount = parts[1] ? decimal + parts[1] : "";
    // Return the concatenation of the formatted integer and fractional parts
    return dollarsAmount + centsAmount;
  }
  // Robustly formats raw cents into a localized currency string according to a specified format pattern
  function formatMoney(cents, format = "") {
    // If the input cents is passed as a string representation, remove any dots beforehand
    if (typeof cents === "string") {
      // Replace all dots in the cents string with an empty string
      cents = cents.replace(".", "");
    }
    // Fall back to theme's configured moneyFormat, currencyFormat, or USD default if no format is provided
    const formatString = format || window.themeHDN?.settings?.moneyFormat || window.themeHDN?.settings?.currencyFormat || "${{amount}}";
    // Initialize a local variable to hold the formatted numerical value
    let value = "";
    // Extract the placeholder type from the format string and match it against the format rules
    switch (formatString.match(placeholderRegex)[1]) {
      // Handle standard currency formatting with two decimal places
      case "amount":
        // Call formatWithDelimiters with 2 decimal precision
        value = formatWithDelimiters(cents, 2);
        // Break out of the switch block
        break;
      // Handle currency formatting without any decimal places
      case "amount_no_decimals":
        // Call formatWithDelimiters with 0 decimal precision
        value = formatWithDelimiters(cents, 0);
        // Break out of the switch block
        break;
      // Handle currency formatting with space as thousands separator and dot as decimal separator
      case "amount_with_space_separator":
        // Call formatWithDelimiters with 2 decimal precision, space thousands, and dot decimal
        value = formatWithDelimiters(cents, 2, " ", ".");
        // Break out of the switch block
        break;
      // Handle currency formatting with dot as thousands separator and comma as decimal separator
      case "amount_with_comma_separator":
        // Call formatWithDelimiters with 2 decimal precision, dot thousands, and comma decimal
        value = formatWithDelimiters(cents, 2, ".", ",");
        // Break out of the switch block
        break;
      // Handle currency formatting with apostrophe as thousands separator and dot as decimal separator
      case "amount_with_apostrophe_separator":
        // Call formatWithDelimiters with 2 decimal precision, apostrophe thousands, and dot decimal
        value = formatWithDelimiters(cents, 2, "'", ".");
        // Break out of the switch block
        break;
      // Handle currency formatting without decimals, with dot as thousands separator and comma as decimal separator
      case "amount_no_decimals_with_comma_separator":
        // Call formatWithDelimiters with 0 decimal precision, dot thousands, and comma decimal
        value = formatWithDelimiters(cents, 0, ".", ",");
        // Break out of the switch block
        break;
      // Handle currency formatting without decimals, using space as thousands separator
      case "amount_no_decimals_with_space_separator":
        // Call formatWithDelimiters with 0 decimal precision and space thousands
        value = formatWithDelimiters(cents, 0, " ");
        // Break out of the switch block
        break;
      // Handle currency formatting without decimals, using apostrophe as thousands separator
      case "amount_no_decimals_with_apostrophe_separator":
        // Call formatWithDelimiters with 0 decimal precision and apostrophe thousands
        value = formatWithDelimiters(cents, 0, "'");
        // Break out of the switch block
        break;
    }
    // Replace the format string placeholder pattern with the fully formatted price value and return it
    return formatString.replace(placeholderRegex, value);
  }

  // Triggers the custom events required by the theme to reload/refresh the cart drawer
  function triggerThemeCartReload() {
    // Dispatch a cart:reload event on the documentElement element
    document.documentElement.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
    // Dispatch a cart:reload event on the document element
    document.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
    // Dispatch a cart:refresh event on the documentElement element
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function toggleCartUpdatingState(isUpdating) {
    const cartDrawer = document.querySelector('hdt-cart-drawer');
    if (cartDrawer) {
      if (isUpdating) {
        cartDrawer.setAttribute('updating', '');
        cartDrawer.style.opacity = '0.5';
        cartDrawer.style.pointerEvents = 'none';
      } else {
        cartDrawer.removeAttribute('updating');
        cartDrawer.style.opacity = '1';
        cartDrawer.style.pointerEvents = 'auto';
      }
    }
  }

  // Synchronize warranty item quantity with current custom ring count in cart
  async function syncWarrantyQuantityWithRings() {
    // Fetch the current cart data asynchronously from cart JSON endpoint
    const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
    // Parse fetched cart response stream as JSON object
    const cart = await cartRes.json();

    // Check if cart contains any Solid Gold items (8K, 10K, 14K, 18K)
    const hasSolidGold = cart.items.some(item => {
      const title = (item.title || item.product_title || '').toUpperCase();
      const variantTitle = (item.variant_title || '').toUpperCase();
      const matProp = (item.properties && item.properties['Material'] ? item.properties['Material'] : '').toUpperCase();
      return title.includes('8K') || title.includes('10K') || title.includes('14K') || title.includes('18K') ||
             variantTitle.includes('8K') || variantTitle.includes('10K') || variantTitle.includes('14K') || variantTitle.includes('18K') ||
             matProp.includes('8K') || matProp.includes('10K') || matProp.includes('14K') || matProp.includes('18K') ||
             matProp.includes('SOLID GOLD');
    });

    // Filter cart items to find existing warranty products
    const warrantyItems = cart.items.filter(item => {
      // Match by variant ID string or product title containing warranty
      return item.variant_id.toString() === '52817939693844' ||
             (item.product_title && item.product_title.toLowerCase().includes('warranty'));
    });

    const warrantyContainer = document.querySelector('.rm-cart-addon-warranty');

    // If solid gold ring is present in cart, automatically hide warranty box and purge/remove all warranty items
    if (hasSolidGold) {
      if (warrantyContainer) warrantyContainer.style.display = 'none';

      if (warrantyItems.length > 0) {
        for (const wItem of warrantyItems) {
          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: wItem.key || wItem.variant_id.toString(),
              quantity: 0
            })
          });
        }
        triggerThemeCartReload();
      }
      return;
    }

    if (warrantyContainer) warrantyContainer.style.display = 'flex';

    // Filter cart items to find those that are custom rings
    const ringItems = cart.items.filter(item => {
      // Check if product type is Custom Ring, or handle contains signet-ring, or properties indicate customization
      return item.product_type === 'Custom Ring' || 
             item.handle.includes('signet-ring') || 
             (item.properties && (item.properties['Color'] || item.properties['Ring Size'] || item.properties['Material']));
    });
    // Sum total quantities of all custom ring items in cart
    const totalRings = ringItems.reduce((acc, item) => acc + item.quantity, 0);

    // Check if any warranty items exist in cart
    if (warrantyItems.length > 0) {
      // Loop through each warranty item found in cart
      for (const wItem of warrantyItems) {
        // Check if total rings is greater than zero and warranty quantity differs from ring count
        if (totalRings > 0 && wItem.quantity !== totalRings) {
          // Send request to change endpoint to update warranty quantity to match total rings
          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: wItem.key || wItem.variant_id.toString(),
              quantity: totalRings
            })
          });
        }
      }
    }
  }

  async function handleWarrantyToggle(checkbox, isChecked) {
    const variantId = checkbox.getAttribute('data-variant-id') || '52817939693844';
    if (!variantId) return;

    toggleCartUpdatingState(true);

    try {
      const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartRes.json();

      const hasSolidGold = cart.items.some(item => {
        const title = (item.title || item.product_title || '').toUpperCase();
        const variantTitle = (item.variant_title || '').toUpperCase();
        const matProp = (item.properties && item.properties['Material'] ? item.properties['Material'] : '').toUpperCase();
        return title.includes('8K') || title.includes('10K') || title.includes('14K') || title.includes('18K') ||
               variantTitle.includes('8K') || variantTitle.includes('10K') || variantTitle.includes('14K') || variantTitle.includes('18K') ||
               matProp.includes('8K') || matProp.includes('10K') || matProp.includes('14K') || matProp.includes('18K') ||
               matProp.includes('SOLID GOLD');
      });

      const warrantyContainer = checkbox.closest('.rm-cart-addon-warranty') || document.querySelector('.rm-cart-addon-warranty');

      if (hasSolidGold) {
        checkbox.checked = false;
        if (warrantyContainer) warrantyContainer.style.display = 'none';

        const warrantyItems = cart.items.filter(item => {
          return item.variant_id.toString() === variantId.toString() ||
                 (item.product_title && item.product_title.toLowerCase().includes('warranty'));
        });

        for (const wItem of warrantyItems) {
          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: wItem.key || wItem.variant_id.toString(),
              quantity: 0
            })
          });
        }

        triggerThemeCartReload();
        toggleCartUpdatingState(false);
        return;
      }

      if (warrantyContainer) warrantyContainer.style.display = 'flex';

      const ringItems = cart.items.filter(item => {
        return item.product_type === 'Custom Ring' || 
               item.handle.includes('signet-ring') || 
               (item.properties && (item.properties['Color'] || item.properties['Ring Size'] || item.properties['Material']));
      });
      const totalRings = ringItems.reduce((acc, item) => acc + item.quantity, 0);
      const addQty = totalRings > 0 ? totalRings : 1;

      if (isChecked) {
        const existingWarranty = cart.items.find(item => {
          return item.variant_id.toString() === variantId.toString() ||
                 (item.product_title && item.product_title.toLowerCase().includes('warranty'));
        });

        if (existingWarranty) {
          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: existingWarranty.key || existingWarranty.variant_id.toString(),
              quantity: addQty
            })
          });
        } else {
          await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: addQty
              }]
            })
          });
        }
      } else {
        const warrantyItems = cart.items.filter(item => {
          return item.variant_id.toString() === variantId.toString() ||
                 (item.product_title && item.product_title.toLowerCase().includes('warranty'));
        });

        for (const wItem of warrantyItems) {
          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: wItem.key || wItem.variant_id.toString(),
              quantity: 0
            })
          });
        }
      }

      triggerThemeCartReload();
      toggleCartUpdatingState(false);
    } catch (err) {
      console.error('Error adding warranty to cart', err);
      toggleCartUpdatingState(false);
    }
  }

  // Prevent accidental CartDrawer-Form submit on non-checkout buttons/inputs
  document.addEventListener('submit', function (e) {
    if (e.target && (e.target.id === 'CartDrawer-Form' || e.target.action?.includes('/cart'))) {
      const submitter = e.submitter || document.activeElement;
      if (!submitter || (submitter.getAttribute('name') !== 'checkout' && !submitter.classList.contains('hdt-mini-cart__btn-checkout'))) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, true);

  // Intercept click on warranty checkbox, box, or label in capture phase
  document.addEventListener('click', function (e) {
    const label = e.target.closest('label[for="rm-warranty-checkbox"]');
    const input = e.target.closest('#rm-warranty-checkbox');
    const box = e.target.closest('#rm-warranty-custom-box');
    const container = e.target.closest('.rm-cart-addon-warranty');

    if (label || input || box || (container && (e.target === container || container.contains(e.target)))) {
      if (e.target.closest('a') || e.target.closest('details')) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const checkbox = document.getElementById('rm-warranty-checkbox');
      if (!checkbox) return;

      let isChecked;
      if (e.target === checkbox) {
        // Direct click on input: browser toggles checkbox.checked prior to click listener
        isChecked = checkbox.checked;
      } else {
        // Click on label, box, or container: manually toggle
        isChecked = !checkbox.checked;
        checkbox.checked = isChecked;
      }

      handleWarrantyToggle(checkbox, isChecked);
    }
  }, true);

  // Intercept change event in capture phase as backup
  document.addEventListener('change', function (e) {
    if (e.target && e.target.id === 'rm-warranty-checkbox') {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  }, true);

  // Inline Cart Editing Logic
  document.addEventListener('click', function (e) {
    if (e.target && e.target.classList.contains('rm-cart-edit-btn')) {
      e.preventDefault();
      const btn = e.target;
      const wrapper = btn.closest('.rm-property-value-wrapper');
      const textSpan = wrapper ? wrapper.querySelector('.rm-property-value-text') : null;
      const selectEl = wrapper ? wrapper.querySelector('.rm-property-edit-select') : null;

      if (textSpan && selectEl) {
        if (selectEl.getAttribute('data-prop') === 'Ring Face Size') {
          const list = selectEl.closest('.hdt-mini-cart_meta_propertyList');
          if (list) {
            let hasMaterial = false;
            let sideEngravings = '';
            list.querySelectorAll('.hdt-product-details__item--property').forEach(li => {
              const lbl = (li.querySelector('.rm-property-label')?.textContent || '').trim();
              const val = (li.querySelector('.rm-property-value-text')?.textContent || '').trim();
              if (lbl === 'Material:') hasMaterial = true;
              if (lbl === 'Side Engravings:') sideEngravings = val;
            });
            if (!hasMaterial && sideEngravings) {
              const isYes = sideEngravings === 'Yes!';
              const mdOpt = selectEl.querySelector('option[value="Medium"]');
              const lgOpt = selectEl.querySelector('option[value="Large"]');
              if (mdOpt) mdOpt.textContent = 'Medium' + (isYes ? ' (+$50)' : ' (+$70)');
              if (lgOpt) lgOpt.textContent = 'Large' + (isYes ? ' (+$100)' : ' (+$130)');
            }
          }
        }
        textSpan.style.display = 'none';
        selectEl.style.display = 'block';
        selectEl.focus(); // Focus so focusout can fire later
        btn.style.display = 'none';
      }
    }
  });

  document.addEventListener('change', async function (e) {
    if (e.target && e.target.classList.contains('rm-property-edit-select')) {
      const selectEl = e.target;
      const detailsEl = selectEl.closest('details');
      if (detailsEl) {
        detailsEl.removeAttribute('open');
        const summary = detailsEl.querySelector('summary');
        if (summary) {
          const originalColor = summary.style.color;
          summary.style.color = '#16a34a';
          setTimeout(() => { if (summary) summary.style.color = originalColor; }, 1000);
        }
      }
      
      const lineIndex = parseInt(selectEl.getAttribute('data-line'));
      const lineKey = selectEl.getAttribute('data-key');
      const propertyKey = selectEl.getAttribute('data-prop');
      const newValue = selectEl.value;

      toggleCartUpdatingState(true);

      try {
        const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
        const cartData = await cartRes.json();

        const item = lineKey ? cartData.items.find(i => i.key === lineKey) : cartData.items[lineIndex - 1];
        if (!item) throw new Error('Item not found');

        const newProperties = { ...item.properties };
        newProperties[propertyKey] = newValue;

        let newVariantId = item.variant_id;
        const productRes = await fetch(item.url.split('?')[0] + '.js');
        if (productRes.ok) {
          const productData = await productRes.json();
          let matchedVariant = null;

          if (newProperties['Material']) {
            let targetMaterial = newProperties['Material'];
            let targetSize = newProperties['Ring Face Size'] || 'Large';
            let targetOverall = newProperties['Overall Hand Engraving'] || 'None';
            if (targetOverall === 'Engraved' && newProperties['Overall Hand Engraving Pattern']) {
              targetOverall = newProperties['Overall Hand Engraving Pattern'];
            }
            
            matchedVariant = productData.variants.find(v => {
              const opts = [v.option1, v.option2, v.option3];
              const matchMat = opts.includes(targetMaterial) ||
                               (targetMaterial === '925 Silver' && opts.includes('Sterling Silver')) ||
                               (targetMaterial === 'Sterling Silver' && opts.includes('925 Silver'));
              return matchMat && opts.includes(targetSize) && opts.includes(targetOverall);
            });
          } else {
            let targetSize = newProperties['Ring Face Size'] || 'Medium';
            let targetEngraving = newProperties['Side Engravings'] || 'Plain';
            let targetColor = newProperties['Color'] || 'Silver';
            
            if (targetSize === 'Deferred') targetSize = 'Large';
            if (targetEngraving === 'Deferred') targetEngraving = 'Plain';

            let checkEngraving = targetEngraving;
            if (checkEngraving === 'Plain') checkEngraving = 'Plain (empty)';

            matchedVariant = productData.variants.find(v => {
              const opts = [v.option1, v.option2, v.option3];
              return opts.includes(targetColor) && opts.includes(targetSize) && (opts.includes(targetEngraving) || opts.includes(checkEngraving));
            });
          }

          if (matchedVariant) {
            newVariantId = matchedVariant.id;
          }
        }

        if (newVariantId !== item.variant_id) {
          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key || lineIndex.toString(),
              quantity: 0
            })
          });
          await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: newVariantId,
              quantity: item.quantity,
              properties: newProperties
            })
          });
        } else {
          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key || lineIndex.toString(),
              quantity: item.quantity,
              properties: newProperties
            })
          });
        }

        // Synchronize warranty item quantity with updated custom ring count
        await syncWarrantyQuantityWithRings();
        // Trigger theme cart reload to update drawer UI
        triggerThemeCartReload();
        // Remove cart updating loading state
        toggleCartUpdatingState(false);
      } catch (err) {
        console.error('Error updating cart property', err);
        toggleCartUpdatingState(false);
      }
    }
  });

  // Explicit Order Note Saving Logic
  document.addEventListener('click', async function (e) {
    const saveBtn = e.target.closest('#rm-cart-note-save-btn');
    if (saveBtn) {
      e.preventDefault();
      const textarea = document.getElementById('Cart-note');
      if (textarea) {
        const note = textarea.value;
        const originalText = saveBtn.innerHTML;
        saveBtn.innerHTML = '<span class="hdt-truncate">Saving...</span>';
        try {
          const res = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: note })
          });
          if (!res.ok) throw new Error('Failed to update note');

          saveBtn.innerHTML = '<span class="hdt-truncate">Saved ✓</span>';

          const display = document.getElementById('rm-cart-note-display');
          if (display) {
            const textSpan = document.getElementById('rm-cart-note-text');
            if (textSpan) {
              textSpan.textContent = note;
            } else {
              display.textContent = `Saved: "${note}"`;
            }
            display.classList.remove('hidden');
          }

          setTimeout(() => { saveBtn.innerHTML = originalText; }, 2000);
        } catch (err) {
          saveBtn.innerHTML = '<span class="hdt-truncate">Error</span>';
          setTimeout(() => { saveBtn.innerHTML = originalText; }, 2000);
        }
      }
    }

  });

  // ---------------------------------------------------------------------------------------------------------------
  // Discount code box (CUSTOM_THEME §10R, Mike's item #24, approved 2026-09-28).
  // Apply: POST /cart/update.js {discount: code}; the cart that comes back says whether the code applies:
  //   "Applied" (the code stays, the button becomes Remove) or "This code doesn't apply to your ring" (the code stays
  //   in the box so a typo can be fixed; the cart's previous codes are put back, Shopify would keep the dead code).
  // Remove: POST /cart/update.js {discount: ""} clears cart- and product-level codes (15DEAL is product-level) and
  //   expires the discount_code cookie that /discount/<code> links leave behind.
  // The drawer markup (lean snippets/cart-contents.liquid, Ecomus sections/cart-drawer.liquid) only knows cart-level
  // codes and is re-rendered after every cart change, so the box state lives here and is put back on every fresh box.
  // Network problems are quiet: the button simply comes back.
  // ---------------------------------------------------------------------------------------------------------------
  const discountUI = { code: '', status: '', draft: '', open: false, busy: false, loaded: false, loading: false, seq: 0 };
  const DISCOUNT_TEXT = { applied: 'Applied', invalid: "This code doesn't apply to your ring" };

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // rmDiscount marks our own requests so the fetch interceptor below does not second-guess the state they set.
  // Each request gets 8 s and one quiet retry: a stalled or refused storefront request must not leave the box busy,
  // and sending the same discount value again gives the same cart (idempotent).
  async function discountRequest(path, body, retried) {
    const init = { headers: { Accept: 'application/json' }, rmDiscount: true };
    if (body) {
      init.method = 'POST';
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 8000) : null;
    if (controller) init.signal = controller.signal;
    try {
      const res = await fetch(cartRoot() + path, init);
      if (!res.ok) throw new Error(path + ' ' + res.status);
      return await res.json();
    } catch (err) {
      console.warn('[discount] ' + path + ' failed' + (retried ? '' : ', retrying'), err && err.message);
      if (retried) throw err;
      await new Promise(r => setTimeout(r, 1200));
      return discountRequest(path, body, true);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  function applicableCodes(cart) {
    return ((cart && cart.discount_codes) || []).filter(d => d && d.applicable && d.code).map(d => String(d.code));
  }

  function expireDiscountCookie() {
    try {
      document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    } catch (e) {}
  }

  function discountMessageEl(input) {
    const form = input.closest('.rm-discount-form') || input.parentNode;
    if (!form || !form.parentNode) return null;
    let msg = form.parentNode.querySelector('.rm-discount-msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'rm-discount-msg';
      msg.setAttribute('role', 'status');
      msg.setAttribute('aria-live', 'polite');
      msg.style.cssText = 'margin: 8px 2px 0; font-size: 1em; line-height: 1.4; font-weight: 500;';
      form.parentNode.insertBefore(msg, form.nextSibling);
    }
    return msg;
  }

  function setDiscountButton(btn, applied) {
    if (!btn) return;
    btn.id = applied ? 'rm-remove-discount-btn' : 'rm-apply-discount-btn';
    btn.textContent = applied ? 'Remove' : 'Apply';
    btn.classList.toggle('is-applied-btn', applied);
    // The Ecomus markup paints its Remove state red inline (!important); both drawers use the theme's .is-applied-btn.
    btn.style.removeProperty('background-color');
    btn.style.removeProperty('border-color');
    btn.style.removeProperty('color');
    btn.style.pointerEvents = '';
    btn.removeAttribute('aria-busy');
  }

  // Puts discountUI onto the box that is in the page now (after every drawer re-render as well).
  function renderDiscountBox() {
    const input = document.getElementById('rm-discount-input');
    if (!input) return;
    input.setAttribute('data-rm-discount', '1');
    const form = input.closest('.rm-discount-form') || input.parentNode;
    const btn = form ? form.querySelector('#rm-apply-discount-btn, #rm-remove-discount-btn') : null;
    const details = input.closest('details');
    if (details && discountUI.open && !details.open) details.open = true;
    if (!discountUI.loaded) return; // until the cart has been read, the server-rendered box is the best we know
    const applied = discountUI.status === 'applied';
    input.readOnly = applied;
    input.classList.toggle('is-applied-input', applied);
    if (applied || discountUI.status === 'invalid') input.value = discountUI.code;
    else input.value = discountUI.draft || '';
    if (!discountUI.busy) setDiscountButton(btn, applied);
    const msg = discountMessageEl(input);
    if (msg) {
      msg.textContent = discountUI.status ? (applied ? '✓ ' : '') + DISCOUNT_TEXT[discountUI.status] : '';
      msg.style.color = applied ? '#047857' : '#4b5563';
      msg.hidden = !discountUI.status;
    }
  }

  // Keeps the state in step with any cart JSON that passes by (cart changes can make a code stop applying).
  function noteCartDiscounts(data) {
    if (!data || !Array.isArray(data.discount_codes) || discountUI.busy) return;
    const live = applicableCodes(data);
    if (live.length) {
      if (discountUI.status !== 'applied' || live.indexOf(discountUI.code) === -1) {
        discountUI.code = live[0];
        discountUI.status = 'applied';
      }
    } else if (discountUI.status === 'applied') {
      const stillThere = data.discount_codes.some(d => d && d.code === discountUI.code);
      discountUI.status = stillThere ? 'invalid' : '';
      if (!stillThere) discountUI.code = '';
    }
    discountUI.loaded = true;
    renderDiscountBox();
  }

  async function loadDiscountState() {
    if (discountUI.loaded || discountUI.loading) return;
    discountUI.loading = true;
    try {
      const cart = await discountRequest('cart.js');
      const live = applicableCodes(cart);
      if (live.length) {
        discountUI.code = live[0];
        discountUI.status = 'applied';
      }
      discountUI.loaded = true;
      renderDiscountBox();
    } catch (e) {
      // quiet: the server-rendered box stays as it is
    } finally {
      discountUI.loading = false;
    }
  }

  function markDiscountBusy(btn, text) {
    if (!btn) return;
    btn.textContent = text;
    btn.style.pointerEvents = 'none';
    btn.setAttribute('aria-busy', 'true');
  }

  async function applyDiscount(btn) {
    const input = document.getElementById('rm-discount-input');
    const code = input ? input.value.trim() : '';
    if (!code || discountUI.busy) return;
    discountUI.busy = true;
    discountUI.open = true;
    discountUI.seq++;
    markDiscountBusy(btn, 'Applying...');
    try {
      let previous = [];
      try {
        previous = applicableCodes(await discountRequest('cart.js')).filter(c => c.toLowerCase() !== code.toLowerCase());
      } catch (e) {}
      const cart = await discountRequest('cart/update.js', { discount: code });
      const entry = (cart.discount_codes || []).find(d => d && String(d.code).toLowerCase() === code.toLowerCase());
      if (entry && entry.applicable) {
        discountUI.code = String(entry.code);
        discountUI.status = 'applied';
      } else {
        discountUI.code = code;
        discountUI.status = 'invalid';
        try { await discountRequest('cart/update.js', { discount: previous.join(',') }); } catch (e) {}
      }
      discountUI.draft = '';
      discountUI.loaded = true;
      discountUI.busy = false;
      renderDiscountBox();
      triggerThemeCartReload();
    } catch (err) {
      discountUI.busy = false;
      discountUI.draft = code;
      setDiscountButton(btn, false);
    }
  }

  async function removeDiscount(btn) {
    if (discountUI.busy) return;
    discountUI.busy = true;
    discountUI.open = true;
    discountUI.seq++;
    markDiscountBusy(btn, 'Removing...');
    try {
      await discountRequest('cart/update.js', { discount: '' });
      expireDiscountCookie();
      discountUI.code = '';
      discountUI.status = '';
      discountUI.draft = '';
      discountUI.loaded = true;
      discountUI.busy = false;
      renderDiscountBox();
      triggerThemeCartReload();
    } catch (err) {
      discountUI.busy = false;
      setDiscountButton(btn, true);
    }
  }

  document.addEventListener('click', function (e) {
    const applyBtn = e.target.closest('#rm-apply-discount-btn');
    const removeBtn = applyBtn ? null : e.target.closest('#rm-remove-discount-btn');
    if (!applyBtn && !removeBtn) return;
    e.preventDefault();
    e.stopPropagation();
    // Decide once, before any await: the old handler renamed Apply to Remove and fell into the Remove branch.
    if (applyBtn) applyDiscount(applyBtn);
    else removeDiscount(removeBtn);
  });

  // Enter in the box applies the code (phone keyboards show "Go").
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || !e.target || e.target.id !== 'rm-discount-input' || e.target.readOnly) return;
    e.preventDefault();
    const btn = document.getElementById('rm-apply-discount-btn');
    if (btn) btn.click();
  });

  // Typing after "doesn't apply" starts over; the typed text survives drawer re-renders.
  document.addEventListener('input', function (e) {
    if (!e.target || e.target.id !== 'rm-discount-input') return;
    discountUI.draft = e.target.value;
    if (discountUI.status === 'invalid') {
      discountUI.status = '';
      discountUI.code = '';
      const msg = discountMessageEl(e.target);
      if (msg) { msg.textContent = ''; msg.hidden = true; }
    }
  });

  // Opening the box reads the cart once (product-level codes such as 15DEAL are not in the server-rendered box).
  document.addEventListener('toggle', function (e) {
    const details = e.target;
    if (!details || !details.classList || !details.classList.contains('rm-discount-accordion')) return;
    discountUI.open = details.open;
    if (details.open) loadDiscountState();
  }, true);

  // A fresh box (drawer re-render) gets the current state back.
  new MutationObserver(function () {
    const input = document.getElementById('rm-discount-input');
    if (input && !input.hasAttribute('data-rm-discount')) renderDiscountBox();
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Capture the original fetch reference from window object
  const originalFetch = window.fetch.bind(window);
  // Intercept all fetch requests globally to seamlessly capture and update the total price container
  window.fetch = async function (...args) {
    // Discount writes that start while this request is in flight make its cart snapshot stale for the discount box
    const discountSeqAtStart = discountUI.seq;
    // Perform the original fetch request and wait for the response
    const response = await originalFetch(...args);
    // Safely extract the target URL of the fetch request
    const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');

    // Determine if the fetch request is a cart write operation
    const isCartWrite = url.includes('cart/change') || url.includes('cart/update') || url.includes('cart/add') || url.includes('cart/clear');
    // Determine if the fetch request contains cart-drawer in its parameters
    const isCartDrawer = url.includes('cart-drawer');
    // Requests made by the discount box itself set its state directly (see discountRequest)
    const ownDiscountRequest = !!(args[1] && args[1].rmDiscount) || discountSeqAtStart !== discountUI.seq;
    // Any other cart read keeps the discount box in step with the cart
    if (!ownDiscountRequest && /\/cart\.js(\?|$)/.test(url)) {
      response.clone().json().then(noteCartDiscounts).catch(() => {});
    }

    // If the request is a write operation or involves the cart drawer
    if (isCartWrite || isCartDrawer) {
      // Clone the response stream to parse it without affecting the main consumer
      const clone = response.clone();
      // Safely fetch the response Content-Type header
      const contentType = clone.headers.get('Content-Type') || '';
      
      // If the response is in JSON format
      if (contentType.includes('application/json')) {
        // Parse the cloned response stream as JSON
        clone.json().then(data => {
          // Keep the discount box in step with the cart that came back
          if (!ownDiscountRequest) noteCartDiscounts(data);
          // Track whether the total price has been successfully updated
          let updatedPrice = false;
          // If the JSON data contains returned sections
          if (data && data.sections) {
            // Find the returned section HTML that contains the custom total price container attribute
            const sectionHtml = Object.values(data.sections).find(html => html && html.includes('data-er-cart-total-price'));
            // If such a section HTML is found
            if (sectionHtml) {
              // Parse the HTML string into a DOM Document object
              const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
              // Extract the total price element from the parsed document
              const newTotalEl = doc.querySelector('[data-er-cart-total-price]');
              // If the total price element exists in the parsed document
              if (newTotalEl) {
                // Set a timeout to let the DOM settle before replacing the total price
                setTimeout(() => {
                  // Find all total price container elements in the current page
                  document.querySelectorAll('[data-er-cart-total-price]').forEach(el => {
                    // Update their innerHTML with the freshly rendered total price HTML
                    el.innerHTML = newTotalEl.innerHTML;
                  });
                // Delay by 100 milliseconds
                }, 100);
                // Mark the updatedPrice flag as true
                updatedPrice = true;
              }
            }
          }
          // If the total price has not been updated yet, and the JSON data contains total_price
          if (!updatedPrice && data && data.total_price !== undefined) {
            // Format the total price cents value using our robust localized currency formatter
            const priceStr = formatMoney(data.total_price, window.themeHDN?.settings?.moneyFormat || window.themeHDN?.settings?.currencyFormat || "${{amount}}");
            // Set a timeout to update the container text Content
            setTimeout(() => {
              // Find all total price container elements in the page
              document.querySelectorAll('[data-er-cart-total-price]').forEach(el => {
                // Set their innerHTML to the formatted price string
                el.innerHTML = priceStr;
              });
            // Delay by 100 milliseconds
            }, 100);
          }
        // Catch any errors during JSON parsing gracefully
        }).catch(err => { });
      // If the response is in HTML format or plain text
      } else {
        // Parse the cloned response stream as text
        clone.text().then(text => {
          // Attempt a try-catch to parse JSON disguised as plain text
          try {
            // Parse the text content as a JSON object
            const data = JSON.parse(text);
            // Keep the discount box in step with the cart that came back
            if (!ownDiscountRequest) noteCartDiscounts(data);
            // If the parsed JSON data contains total_price
            if (data && data.total_price !== undefined) {
              // Format the total price cents using the localized currency formatter
              const priceStr = formatMoney(data.total_price, window.themeHDN?.settings?.moneyFormat || window.themeHDN?.settings?.currencyFormat || "${{amount}}");
              // Set a timeout to update the price in the DOM
              setTimeout(() => {
                // Find all total price elements in the page
                document.querySelectorAll('[data-er-cart-total-price]').forEach(el => {
                  // Update their innerHTML with the formatted price string
                  el.innerHTML = priceStr;
                });
              // Delay by 100 milliseconds
              }, 100);
              // Immediately exit the text promise handler
              return;
            }
          // Fail silently if text is indeed HTML and not JSON
          } catch(e) {}
          
          // Parse the text as a DOM Document object
          const html = new DOMParser().parseFromString(text, 'text/html');
          // Extract the total price element from the parsed document
          const newTotalEl = html.querySelector('[data-er-cart-total-price]');
          // If the total price element exists in the document
          if (newTotalEl) {
            // Set a timeout to apply the update in the DOM
            setTimeout(() => {
              // Find all total price elements in the page
              document.querySelectorAll('[data-er-cart-total-price]').forEach(el => {
                // Update their innerHTML with the new total price HTML
                el.innerHTML = newTotalEl.innerHTML;
              });
            // Delay by 50 milliseconds
            }, 50);
          }
        // Catch any errors during text parsing gracefully
        }).catch(err => { });
      }
    }

    // Return the original response stream to the caller
    return response;
  };
})();

// Social Proof Reviews Logic
(function initSocialProof() {
  const reviews = [
    { text: '"The engraving is flawless. Completely exceeded my expectations."', author: '— Freddie Collins' },
    { text: '"Beautiful craftsmanship! The heavy gold weight feels incredibly premium."', author: '— Michael Scott' },
    { text: '"Customer service was amazing and the ring arrived faster than expected."', author: '— David Baker' }
  ];

  let reviewIndex = 0;
  let autoPlayInterval = null;

  function updateReview(index, swipeArea) {
    if (!swipeArea) return;
    const textContainer = swipeArea.querySelector('.rm-social-proof-text');
    const dots = swipeArea.querySelectorAll('.rm-dot');
    if (!textContainer || !dots.length) return;

    reviewIndex = index;
    textContainer.style.opacity = 0;

    dots.forEach((dot, i) => {
      dot.style.background = i === index ? '#10b981' : '#d1d5db';
    });

    setTimeout(() => {
      const review = reviews[index];
      textContainer.innerHTML = review.text + '<div style="font-weight: 600; font-style: normal; font-size: 13px; margin-top: 4px;">' + review.author + '</div>';
      textContainer.style.opacity = 1;
    }, 300);
  }

  function nextReview(swipeArea) {
    updateReview((reviewIndex + 1) % reviews.length, swipeArea);
  }

  function prevReview(swipeArea) {
    updateReview((reviewIndex - 1 + reviews.length) % reviews.length, swipeArea);
  }

  function startAutoPlay(swipeArea) {
    if (autoPlayInterval) clearInterval(autoPlayInterval);
    autoPlayInterval = setInterval(() => nextReview(swipeArea), 5000);
  }

  function initOrRestart() {
    const swipeArea = document.querySelector('.rm-social-proof');
    if (swipeArea) {
      startAutoPlay(swipeArea);
    } else {
      if (autoPlayInterval) clearInterval(autoPlayInterval);
    }
  }

  document.addEventListener('DOMContentLoaded', initOrRestart);
  document.addEventListener('cart:refresh', () => setTimeout(initOrRestart, 500));

  document.addEventListener('click', (e) => {
    if (e.target.matches('.rm-social-proof .rm-dot')) {
      const swipeArea = e.target.closest('.rm-social-proof');
      updateReview(parseInt(e.target.dataset.index), swipeArea);
      startAutoPlay(swipeArea);
    }
  });

  let touchstartX = 0;
  let touchendX = 0;
  let isDragging = false;

  document.addEventListener('mousedown', e => {
    const swipeArea = e.target.closest('.rm-social-proof');
    if (swipeArea) {
      isDragging = true;
      touchstartX = e.clientX;
      swipeArea.style.cursor = 'grabbing';
    }
  });

  document.addEventListener('mouseup', e => {
    if (!isDragging) return;
    const swipeArea = e.target.closest('.rm-social-proof');
    if (swipeArea) {
      touchendX = e.clientX;
      swipeArea.style.cursor = 'grab';
      isDragging = false;
      if (touchstartX - touchendX > 30) { nextReview(swipeArea); startAutoPlay(swipeArea); }
      else if (touchendX - touchstartX > 30) { prevReview(swipeArea); startAutoPlay(swipeArea); }
    } else {
      isDragging = false;
    }
  });

  document.addEventListener('touchstart', e => {
    const swipeArea = e.target.closest('.rm-social-proof');
    if (swipeArea) {
      isDragging = true;
      touchstartX = e.changedTouches[0].screenX;
    }
  }, { passive: true });

  document.addEventListener('touchend', e => {
    if (!isDragging) return;
    const swipeArea = e.target.closest('.rm-social-proof');
    if (swipeArea) {
      touchendX = e.changedTouches[0].screenX;
      isDragging = false;
      if (touchstartX - touchendX > 30) { nextReview(swipeArea); startAutoPlay(swipeArea); }
      else if (touchendX - touchstartX > 30) { prevReview(swipeArea); startAutoPlay(swipeArea); }
    } else {
      isDragging = false;
    }
  }, { passive: true });

  const observer = new MutationObserver((mutations) => {
    for (let m of mutations) {
      if (m.addedNodes.length) {
        const added = Array.from(m.addedNodes).some(n => n.nodeType === 1 && (n.classList.contains('rm-social-proof') || n.querySelector('.rm-social-proof')));
        if (added) {
          initOrRestart();
          break;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
